import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { RFC, FluxStandardAction as Action } from 'state/StateTypes';
import BaseButton from './BaseButton';
import { StateItems } from 'state/assets/AssetTypes';
import { SearchField, SortField, SortOrder, UserRole } from 'state/user/UserTypes';
import { InputChange, ReactSelectValue, ReactSelectOption, Error, FetchRequest } from 'types';
import Select from 'components/Select';
import { getCustomersAsCSV } from 'state/user/UserActions';
import ButtonLoadingSpinner from 'components/ButtonLoadingSpinner';
import { CSVLink } from 'react-csv';
import InputWithDropdown from './InputWithDropdown';
import useViewport from 'hooks/useViewport';
import ClearInputButton from './ClearInputButton';
import { Link } from '@reach/router';
import { pluralize } from 'utils';
import { useDispatch } from 'react-redux';
import * as queryActions from 'state/query/QueryActionTypes';
import { QueryState } from 'state/query/QueryReducer';
import * as userActions from 'state/users/UsersActionTypes';

interface Props {
   title: StateItems;
   query: QueryState;
   viewingUsersWithRole: UserRole;
   isLoading: boolean;
   setParentLoadingState: () => void;
}

const ListHeader: RFC<Props> = ({ title, query, viewingUsersWithRole, isLoading, setParentLoadingState }) => {
   const [searchString, setSearchString] = useState<string>('');
   const [searchField, setSearchField] = useState<SearchField>(query.searchField);
   const [sortField, setSortField] = useState<SortField>(query.sortField);
   const [sortOrder, setSortOrder] = useState<SortOrder>(query.sortOrder);
   const [sortLoading, setSortLoading] = useState<{ field: boolean; order: boolean }>({ field: false, order: false });
   const [csvData, setCsvData] = useState<FetchRequest<string, Error | null>>({ fetching: false, data: '', error: null });
   const [{ width: windowWidth }] = useViewport();
   const csvLink = useRef<CSVLink>(null);
   const searchInput = useRef<HTMLInputElement>(null);
   const dispatch = useDispatch();
   const [showInputLoadingState, setShowInputLoadingState] = useState<boolean>(false);
   //when the user role (aka query.type) changes, reset search input
   useEffect(() => {
      setSearchString('');
   }, [query.type]);

   useEffect(() => {
      if (!!query.search) {
         setSearchString(query.search)
      }

      if (!!searchField && !query.search) {
         setSearchString("")
      }
      setSearchField(query.searchField);
      setSortField(query.sortField);
      setSortOrder(query.sortOrder);
   }, [query.search, query.searchField, query.sortField, query.sortOrder])

   useEffect(() => {
      (async () => {
         const actions: Action[] = []
         if (query.searchField !== searchField) {
            actions.push(...[
               dispatch({ type: queryActions.SET_QUERY_SEARCH_FIELD, payload: searchField }),
               dispatch({ type: userActions.SET_IS_VIEWING_USER_DETAILS, payload: false })
            ])
         }
         if (query.sortField !== sortField) {
            actions.push(...[
               dispatch({ type: userActions.CLEAR_USERS }),
               dispatch({ type: userActions.SET_IS_VIEWING_USER_DETAILS, payload: false }),
               dispatch({ type: queryActions.SET_QUERY_PAGE, payload: 1 }),
               dispatch({ type: queryActions.SET_QUERY_SORT_FIELD, payload: sortField })
            ])
         }
         if (query.sortOrder !== sortOrder) {
            actions.push(...[
               dispatch({ type: userActions.CLEAR_USERS }),
               dispatch({ type: userActions.SET_IS_VIEWING_USER_DETAILS, payload: false }),
               dispatch({ type: queryActions.SET_QUERY_PAGE, payload: 1 }),
               dispatch({ type: queryActions.SET_QUERY_SORT_ORDER, payload: sortOrder })
            ])
         }
         !!actions.length && (await Promise.all(actions))
      })()
   }, [searchField, sortField, sortOrder]);

   useEffect(() => {
      const handleEnterClick = (e: KeyboardEvent) => {
         if (e.keyCode === 13 && !!searchString) {
            search();
            searchInput.current?.blur()
         }
      };
      window.addEventListener('keyup', handleEnterClick);

      return () => {
         window.removeEventListener('keyup', handleEnterClick);
      };
   }, [searchString]); 

   const handleSearchInput = (e: InputChange) => {
      e.persist();
      setSearchString(e.target.value);
   };

   const clearSearchAndRefireQuery = async (): Promise<void> => {
      await setShowInputLoadingState(true);
      await setParentLoadingState();
      setSearchString('');
      await dispatch({ type: queryActions.RESET_QUERY })
      setTimeout(() => {
         setShowInputLoadingState(false)
      }, isLoading ? 1200 : 500)
   };

   const search = async (): Promise<void> => {
      if (query.search !== searchString) {
         await setShowInputLoadingState(true)
         const search = searchField === SearchField.email ? encodeURIComponent(searchString) : searchString.trim()
         const actions: Action[] = [
            dispatch({ type: userActions.CLEAR_USERS }),
            dispatch({ type: userActions.SET_IS_VIEWING_USER_DETAILS, payload: false }),
            dispatch({ type: queryActions.SET_QUERY_PAGE, payload: 1 })
         ]
         await Promise.all(actions).then(() => dispatch({ type: queryActions.SET_QUERY_SEARCH, payload: search }))
         setTimeout(() => {
            setShowInputLoadingState(false)
         }, isLoading ? 1200 : 500)
      }
   };

   const handleSearchField = (searchField: ReactSelectValue) => {
      setSearchField((searchField as ReactSelectOption).value as SearchField);
   };

   const handleSortField = async (sortField: ReactSelectValue) => {
      await setSortLoading(prev => ({ ...prev, field: true }));
      setSortField((sortField as ReactSelectOption).value as SortField);
      setTimeout(async () => {
         await setSortLoading(prev => ({ ...prev, field: false }));
      }, 1000);
   };

   const handleSortOrder = async (sortOrder: ReactSelectValue) => {
      await setSortLoading(prev => ({ ...prev, order: true }));
      setSortOrder((sortOrder as ReactSelectOption).value as SortOrder);
      setTimeout(async () => {
         await setSortLoading(prev => ({ ...prev, order: false }));
      }, 1000);
   };

   const getCustomerCSVData = async (): Promise<void> => {
      try {
         await setCsvData(prev => ({ ...prev, fetching: true }));
         const data = await getCustomersAsCSV();
         await setCsvData(prev => ({ ...prev, data }));
         //@ts-ignore ¯\_(ツ)_/¯
         if (csvLink.current && csvLink.current.link) {
            //@ts-ignore
            csvLink.current.link.click();
         }
      } catch (error) {
         error = error.json ? await error.json() : error;
         console.log(error);
         setCsvData(prev => ({ ...prev, error }));
      }
      setCsvData(prev => ({ ...prev, fetching: false }));
   };

   const searchFieldOptions: ReactSelectOption[] = [
      { value: SearchField.lastName, label: 'Last name' },
      { value: SearchField.firstName, label: 'First name' },
      { value: SearchField.email, label: 'Email' },
      { value: SearchField.phone, label: 'Phone' },
   ];
   
   const sortFieldOptions: ReactSelectOption[] = [
      { value: SortField.lastName, label: 'Last name' },
      { value: SortField.firstName, label: 'First name' },
      { value: SortField.email, label: 'Email' },
      { value: SortField.createdAt, label: 'Created at' }
   ];

   const sortOrderOptions: ReactSelectOption[] = [
      { value: SortOrder.ascending, label: 'Ascending' }, 
      { value: SortOrder.descending, label: 'Descending' }
   ]

   return (
      <>
         <Container>
            {windowWidth > 590 ? (
               <TitleContainer>
                  <ListTitle>{pluralize(title).toLocaleUpperCase()}</ListTitle>
                  {viewingUsersWithRole === 'admin' ? (
                     <ButtonLinkWrapper to="/admins/create">
                        <Button>CREATE</Button>
                     </ButtonLinkWrapper>
                  ) : (
                     <>
                        <ExportAsCSVButton onClick={getCustomerCSVData} isCustomerList={viewingUsersWithRole === 'customer'}>
                           <ButtonLoadingSpinner isLoading={csvData.fetching} />
                           <LoaderButtonText isLoading={csvData.fetching}>Export all data as CSV</LoaderButtonText>
                        </ExportAsCSVButton>
                        <HiddenCSVLink data={csvData.data} filename="neuvana_customer_data.csv" ref={csvLink} />
                     </>
                  )}
               </TitleContainer>
            ) : (
               <ListTitle>{title.toLocaleUpperCase()}</ListTitle>
            )}
            <FlexWrapper>
               {windowWidth > 590 && (
                  <InputWithDropdown
                     inputValue={searchString}
                     inputOnChange={handleSearchInput}
                     clearSearchAndRefireQuery={clearSearchAndRefireQuery}
                     clearInputValue={() => setSearchString('')}
                     isPreviousSearch={!!query.search}
                     dropdownPosition="right"
                     inputPlaceholder={`Search ${title}s by`}
                     buttonText="search"
                     selectOptions={searchFieldOptions}
                     selectOnChange={handleSearchField}
                     selectValue={searchFieldOptions.find(({ value }) => searchField === value) || searchFieldOptions[0]}
                     onClick={search}
                     showInputLoadingState={showInputLoadingState}
                     searchDisabled={!searchString}
                     ref={searchInput}
                  />
               )}
               {windowWidth <= 590 && (
                  <MobileInputContainer>
                     <SearchInput placeholder="Search customers" value={searchString} onChange={handleSearchInput} ref={searchInput} />
                     <ClearInputButton onClick={!!query.search ? clearSearchAndRefireQuery : () => setSearchString('')} visible={!!searchString} />
                     <Button onClick={search} disabled={!searchString}>
                        SEARCH
                     </Button>
                  </MobileInputContainer>
               )}
               <SelectContainer>
                  {windowWidth <= 590 && <Select label="Search by" onChange={handleSearchField} options={searchFieldOptions} />}
                  <Select
                     label="Sort by"
                     onChange={handleSortField}
                     isLoading={sortLoading.field}
                     selectedOption={sortFieldOptions.find(({ value }) => value === sortField)}
                     options={sortFieldOptions}
                  />
                  <Select
                     label="Sort order"
                     onChange={handleSortOrder}
                     isLoading={sortLoading.order}
                     selectedOption={sortOrderOptions.find(({ value }) => value === sortOrder)}
                     options={sortOrderOptions}
                  />
               </SelectContainer>
            </FlexWrapper>
         </Container>
         {windowWidth <= 590 && (
            <>
               <ExportAsCSVButton onClick={getCustomerCSVData} isCustomerList={viewingUsersWithRole === 'customer'}>
                  <ButtonLoadingSpinner isLoading={csvData.fetching} />
                  <LoaderButtonText isLoading={csvData.fetching}>Export all data as CSV</LoaderButtonText>
               </ExportAsCSVButton>
               <HiddenCSVLink data={csvData.data} filename="neuvana_customer_data.csv" ref={csvLink} />
            </>
         )}
      </>
   );
};

export default ListHeader;

const Container = styled.div`
   display: flex;
   flex-wrap: wrap;
   justify-content: space-between;
   flex-direction: row nowrap;
   align-items: center;
`;

const ListTitle = styled.h2`
   font-weight: 700;
   font-size: 2em;
   color: ${({ theme }) => theme.primaryBlueDark};
   padding: 5px;
   margin: auto 5px 0 0;
   @media only screen and (max-width: 590px) {
      margin: unset;
   }
`;

const Button = styled(BaseButton)`
   width: auto;
   padding: 10px 15px;
   margin: unset;
   margin-left: 10px;
   flex-grow: 0;

   @media only screen and (max-width: 590px) {
      margin-bottom: 7px;
   }
   &:focus {
      border: none;
   }
`;

const ButtonLinkWrapper = styled(Link)`
   text-decoration: none;
`;

const LoaderButtonText = styled.span<{ isLoading: boolean }>`
   font-weight: bold;
   transition: opacity 200ms;
   width: 100%;
   transition-delay: ${({ isLoading }: { isLoading: boolean }) => (isLoading ? '0ms' : '200ms')};
   opacity: ${({ isLoading }: { isLoading: boolean }) => (isLoading ? 0 : 1)};
`;

const SearchInput = styled.input.attrs({ type: 'text' })`
   font-size: 0.9em;
   color: ${({ theme }) => theme.almostBlack};
   background: white;
   width: 50%;
   height: 28px;
   border: 1px solid ${({ theme }) => theme.primaryGrey};
   box-sizing: border-box;
   padding: 20px;
   border-radius: 10px;
   flex-grow: 1;
   ::placeholder {
      color: ${({ theme }) => theme.primaryGrey};
      opacity: 0.9;
      font-style: italic;
   }
   @media only screen and (max-width: 590px) {
      margin-bottom: 7px;
   }
`;

const FlexWrapper = styled.div`
   display: flex;
   flex-flow: row wrap;
   justify-content: center;
   align-items: center;
   flex-grow: 1;
   max-width: 600px;
`;

const SelectContainer = styled.div`
   display: flex;
   margin-top: 1.5em;
   flex-flow: row wrap;
   justify-content: flex-end;
   align-items: center;
   width: 100%;
   > * {
      margin: 0 8px;
   }

   @media only screen and (max-width: 590px) {
      display: block;
      > * {
         margin: unset;
      }
   }
`;

const ExportAsCSVButton = styled.button<{ isCustomerList: boolean }>`
   visibility: ${({ isCustomerList }) => (isCustomerList ? 'inherit' : 'hidden')};
   background-color: ${({ theme }) => theme.secondaryGreen};
   position: relative;
   color: white;
   padding: 6px 8px;
   border-radius: 4px;
   border: none;
   font-size: 15px;
   font-weight: 700;
   margin-left: 8px;
   &:focus {
      outline: none;
   }
   &:hover {
      background-color: ${({ theme }) => theme.primaryGreen};
      cursor: pointer;
      color: white;
   }

   @media only screen and (max-width: 590px) {
      display: inline-block;
      margin-left: unset;
      margin-bottom: 5px;
   }
`;

const HiddenCSVLink = styled(CSVLink)`
   display: 'none';
`;

const TitleContainer = styled.div`
   display: flex;
   flex-direction: column;
   justify-content: flex-start;
   align-items: flex-start;
   align-content: flex-start;
   padding-bottom: 8px;
   margin-bottom: 4em;
`;

const MobileInputContainer = styled.div`
   display: flex;
   flex-direction: row;
   width: 100%;
   justify-content: flex-start;
   align-items: center;
   position: relative;
`;